/**
 * Badge API utility.
 * Updates the PWA app icon badge with unread notification count.
 * Uses navigator.setAppBadge / clearAppBadge where supported (Chrome, Edge).
 * iOS: no-op — in-app bell icon shows count instead.
 */

/**
 * Check if the Badge API is supported in the current browser.
 * @returns true if setAppBadge is available
 */
export function isBadgeSupported(): boolean {
  return 'setAppBadge' in navigator;
}

/**
 * Update the app icon badge with the given count.
 * No-op on unsupported browsers (iOS, Firefox, etc.).
 * @param count - Unread notification count
 */
export async function updateBadge(count: number): Promise<void> {
  if (!isBadgeSupported()) return;

  try {
    if (count > 0) {
      await navigator.setAppBadge(count);
    } else {
      await navigator.clearAppBadge();
    }
  } catch (error) {
    console.warn('Badge API error:', error);
  }
}

/**
 * Clear the app icon badge.
 * No-op on unsupported browsers.
 */
export async function clearBadge(): Promise<void> {
  if (!isBadgeSupported()) return;

  try {
    await navigator.clearAppBadge();
  } catch (error) {
    console.warn('Badge API clear error:', error);
  }
}